import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, Phone, MapPin, Globe, CheckCircle } from 'lucide-react';

const FacilityCard = ({ facility, showState = false }) => {
  return (
    <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-l-4 border-l-blue-500">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
              {facility.name}
            </h3>
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4 mr-1" />
              <span>
                {facility.city}, {showState ? facility.stateFullName : facility.state}
              </span>
            </div>
          </div>
          
          {facility.rating && (
            <div className="flex items-center space-x-1 text-sm">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{facility.rating}</span>
              <span className="text-gray-500">({facility.reviews})</span>
            </div>
          )}
        </div>
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
          {facility.description}
        </p>
        
        {/* Treatment Types */}
        <div className="flex flex-wrap gap-1 mb-3">
          {facility.treatmentTypes?.slice(0, 3).map((type) => (
            <Badge key={type} variant="secondary" className="text-xs">
              {type}
            </Badge>
          ))}
          {facility.treatmentTypes?.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{facility.treatmentTypes.length - 3} more
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Specializations */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Specializes in:</h4>
          <div className="flex flex-wrap gap-1">
            {facility.specializations?.slice(0, 2).map((spec) => (
              <Badge key={spec} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                {spec}
              </Badge>
            ))}
          </div>
        </div>

        {/* Features */}
        {facility.features && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
            <div className="space-y-1">
              {facility.features.slice(0, 2).map((feature) => (
                <div key={feature} className="flex items-center text-xs text-gray-600">
                  <CheckCircle className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insurance */}
        {facility.insurance && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Accepts Insurance:</h4>
            <div className="flex flex-wrap gap-1">
              {facility.insurance.slice(0, 3).map((ins) => (
                <Badge key={ins} variant="outline" className="text-xs">
                  {ins}
                </Badge>
              ))}
              {facility.insurance.length > 3 && (
                <span className="text-xs text-gray-500">+{facility.insurance.length - 3} more</span>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-4 border-t bg-gray-50 flex flex-col space-y-2">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-3 text-sm">
            {facility.phone && (
              <a 
                href={`tel:${facility.phone}`}
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Phone className="h-4 w-4 mr-1" />
                <span className="font-medium">Call Now</span>
              </a>
            )}
            
            {facility.website && (
              <a 
                href={facility.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <Globe className="h-4 w-4 mr-1" />
                <span>Website</span>
              </a>
            )}
          </div>
        </div>
        
        <Button asChild className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
          <Link to={`/facility/${facility.id}`}>
            View Details & Get Help
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default FacilityCard;